<template>
  <div
    ref="shellEl"
    class="global-chat"
    :class="{
      open: chat.open,
      'over-duel': chat.duelAvailable,
      compact,
      'step-medium': compact && compactStep === 'medium',
      'step-full': compact && compactStep === 'full',
      placed: anchor !== null,
      'grow-right': panelGrow.growRight,
      'grow-down': panelGrow.growDown,
      dragging,
    }"
    :style="shellStyle"
  >
    <div
      v-if="chat.open"
      ref="panelEl"
      class="global-chat-panel"
      :style="panelStyle"
    >
      <button
        v-if="!compact"
        type="button"
        class="global-chat-grip"
        :aria-label="t('chat.global.resize')"
        :title="t('chat.global.resize')"
        @pointerdown="onGripDown"
        @keydown="onGripKey"
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <path
            fill="currentColor"
            d="M14 10.5 5.5 2 4 3.5 12.5 12 14 10.5zm0 3L2.5 2 1 3.5 12.5 15 14 13.5z"
          />
        </svg>
      </button>
      <div class="global-chat-head">
        <div v-if="showChannelTabs" class="global-chat-tabs" role="tablist">
          <button
            type="button"
            class="global-chat-tab"
            :class="{ active: chat.tab === 'global' }"
            role="tab"
            :aria-selected="chat.tab === 'global'"
            @click="chat.setTab('global')"
          >
            {{ t('chat.global.tab') }}
            <span
              v-if="chat.tab !== 'global' && chat.globalUnread > 0"
              class="global-chat-tab-badge"
            >
              {{ chat.globalUnread > 9 ? '9+' : chat.globalUnread }}
            </span>
          </button>
          <button
            v-if="chat.duelAvailable"
            type="button"
            class="global-chat-tab"
            :class="{ active: chat.tab === 'duel' }"
            role="tab"
            :aria-selected="chat.tab === 'duel'"
            @click="chat.setTab('duel')"
          >
            {{ t('chat.duel.tab') }}
            <span
              v-if="chat.tab !== 'duel' && chat.duelUnread > 0"
              class="global-chat-tab-badge"
            >
              {{ chat.duelUnread > 9 ? '9+' : chat.duelUnread }}
            </span>
          </button>
          <div
            v-for="name in chat.dmTabs"
            :key="name"
            class="dm-tab"
            :class="{ active: chat.tab === 'dm' && chat.dmActive === name }"
          >
            <button
              type="button"
              class="global-chat-tab dm-tab-btn"
              role="tab"
              :aria-selected="chat.tab === 'dm' && chat.dmActive === name"
              @click="chat.focusDm(name)"
            >
              {{ name }}
              <span
                v-if="!(chat.tab === 'dm' && chat.dmActive === name) && chat.dmUnreadOf(name) > 0"
                class="global-chat-tab-badge"
              >
                {{ chat.dmUnreadOf(name) > 9 ? '9+' : chat.dmUnreadOf(name) }}
              </span>
            </button>
            <button
              type="button"
              class="dm-tab-x"
              :aria-label="t('chat.dm.close', { name })"
              @click="chat.closeDm(name)"
            >
              ×
            </button>
          </div>
        </div>
        <div v-else class="global-chat-title">{{ t('chat.global.title') }}</div>
        <output
          v-if="onlineCount !== null && chat.tab !== 'dm'"
          class="global-chat-online"
          :aria-label="onlineLabel"
          :title="onlineLabel"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z"
            />
          </svg>
          <span>{{ onlineCount }}</span>
        </output>
        <button
          type="button"
          class="global-chat-size"
          :title="sizeLabel"
          :aria-label="sizeLabel"
          :aria-pressed="compact ? compactStep !== 'normal' : isEnlarged"
          @click="toggleSize"
        >
          <svg
            v-if="!shrinkIcon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M4 9V4h5v2H6v3H4zm11-5h5v5h-2V6h-3V4zM4 15h2v3h3v2H4v-5zm13 3v-3h2v5h-5v-2h3z"
            />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M9 4v3H6v2h5V4H9zm6 0v5h5V7h-3V4h-2zM4 13v2h3v3h2v-5H4zm11 0v5h2v-3h3v-2h-5z"
            />
          </svg>
        </button>
      </div>
      <div class="global-chat-log-wrap">
        <div ref="logEl" class="global-chat-log" @scroll.passive="syncAwayFromBottom">
          <div
            v-for="msg in visibleLines"
            :key="msg.id"
            class="global-chat-line"
            :class="{ mine: msg.mine, notice: msg.notice }"
          >
            <p v-if="msg.notice" class="global-chat-notice" role="status">{{ msg.text }}</p>
            <AvatarView
              v-else
              :config="msg.avatar"
              crop="bust"
              circle
              :size="36"
            />
            <div v-if="!msg.notice" class="global-chat-body">
              <router-link
                class="global-chat-name"
                :to="publicProfilePath(msg.username)"
                :title="t('chat.global.profile', { name: msg.username })"
                :aria-label="t('chat.global.profile', { name: msg.username })"
              >{{ msg.username }}</router-link>
              <span class="global-chat-text">
                <template v-for="(part, i) in msg.parts" :key="i">
                  <router-link
                    v-if="part.kind === 'mention'"
                    class="global-chat-mention"
                    :class="{ me: part.me }"
                    :to="publicProfilePath(part.username)"
                    :title="t('chat.global.profile', { name: part.username })"
                    :aria-label="t('chat.global.profile', { name: part.username })"
                  >@{{ part.username }}</router-link>
                  <template v-else>{{ part.text }}</template>
                </template>
              </span>
            </div>
          </div>
        </div>
        <button
          v-if="awayFromBottom"
          type="button"
          class="global-chat-jump"
          @click="jumpToLatest"
        >
          {{ t('chat.global.latest') }}
          <span v-if="jumpUnread > 0" class="global-chat-jump-count">
            {{ jumpUnread > 9 ? '9+' : jumpUnread }}
          </span>
        </button>
      </div>
      <div
        v-if="mentionOpen"
        id="global-chat-mentions"
        class="global-chat-mentions"
        :aria-label="t('chat.global.mentionList')"
      >
        <p v-if="suggestions.length === 0" class="global-chat-mention-empty">
          {{ mentionQuery ? t('chat.global.mentionMiss') : t('chat.global.mentionNone') }}
        </p>
        <button
          v-for="(name, i) in suggestions"
          :key="name"
          type="button"
          class="global-chat-mention-opt"
          :class="{ active: i === mentionIndex }"
          @mousedown.prevent="pickMention(name)"
        >
          @{{ name }}
        </button>
      </div>
      <form class="global-chat-form" @submit.prevent="onSubmit">
        <input
          id="global-chat-input"
          ref="inputEl"
          v-model="draft"
          class="global-chat-input"
          type="text"
          maxlength="200"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="mentionOpen"
          aria-controls="global-chat-mentions"
          :placeholder="placeholder"
          :aria-label="placeholder"
          @keydown.stop="onInputKeydown"
          @input="syncCursor"
          @keyup="syncCursor"
          @click="syncCursor"
          @select="syncCursor"
        />
        <button type="submit" class="global-chat-send">
          {{ t('chat.global.send') }}
        </button>
      </form>
    </div>
    <button
      ref="toggleEl"
      type="button"
      class="global-chat-toggle"
      :title="toggleTitle"
      :aria-label="toggleTitle"
      @pointerdown="onTogglePointerDown"
      @click="onToggleClick"
      @keydown="onToggleKey"
    >
      <span v-if="!compact">{{ toggleLabel }}</span>
      <span
        v-if="!compact && !chat.open && onlineCount !== null"
        class="global-chat-online global-chat-online-toggle"
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24" width="14" height="14">
          <path
            fill="currentColor"
            d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z"
          />
        </svg>
        <span>{{ onlineCount }}</span>
      </span>
      <span v-else-if="compact" class="chat-fab-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path
            d="M4 4h16v12H7l-3 3V4zm3 5h10v2H7V9zm0 4h7v2H7v-2z"
          />
        </svg>
      </span>
      <span v-if="chat.closedBadge > 0" class="global-chat-badge">
        {{ chat.closedBadge > 9 ? '9+' : chat.closedBadge }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  activeMentionQuery,
  chatNamesMatch,
  mentionSuggestions,
  parseAvatarConfig,
  splitChatMentions,
  type ChatTextPart,
  type GlobalChatMessagePayload,
} from '@shared/index'
import { ApiError } from '../api/http'
import { publicProfilePath } from '../api/profile'
import { getClientId } from '../game/net/clientId'
import { useI18n } from '../i18n/t'
import { useAuthStore } from '../stores/auth'
import { useChatStore } from '../stores/chat'
import { useChatMuteStore } from '../stores/chatMutes'
import { useFriendsStore } from '../stores/friends'
import { usePlayerStore } from '../stores/player'
import AvatarView from './AvatarView.vue'

const props = defineProps<{
  messages: GlobalChatMessagePayload[]
  compact?: boolean
  onlineCount: number | null
  onlineUsers: string[]
}>()

const emit = defineEmits<{
  send: [text: string]
}>()

const SIZE_KEY = 'dc-chat-size'
const COMPACT_STEP_KEY = 'dc-chat-compact-step'
const POS_KEY = 'dc-chat-pos'
const EDGE = 8
const DEFAULT_W = 380
const DEFAULT_H = 340
const MIN_W = 300
const MIN_H = 220
const ABS_MAX_W = 960
const ABS_MAX_H = 900

type CompactStep = 'normal' | 'medium' | 'full'
type ChatAnchor = { x: number; y: number; rx: number; ry: number }

const { t } = useI18n()
const chat = useChatStore()
const friends = useFriendsStore()
const mutes = useChatMuteStore()
const auth = useAuthStore()
const player = usePlayerStore()
const myClientId = getClientId()
const draft = ref('')
const inputEl = ref<HTMLInputElement | null>(null)
const cursor = ref(0)
const mentionIndex = ref(0)
const dismissedQuery = ref<string | null>(null)
const logEl = ref<HTMLElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)
const shellEl = ref<HTMLElement | null>(null)
const toggleEl = ref<HTMLButtonElement | null>(null)
const requested = ref(readChatSize())
const compactStep = ref<CompactStep>(readCompactStep())
const anchor = ref<ChatAnchor | null>(readAnchor())
const kbOffset = ref(0)
const btnSize = ref({ w: 48, h: 48 })
const footprint = ref({ w: 0, h: 0 })
const dragging = ref(false)
let suppressToggle = false

function readCompactStep(): CompactStep {
  try {
    const raw = localStorage.getItem(COMPACT_STEP_KEY)
    if (raw === 'medium' || raw === 'full' || raw === 'normal') return raw
  } catch {
    /* private mode */
  }
  return 'normal'
}

function persistCompactStep(): void {
  try {
    localStorage.setItem(COMPACT_STEP_KEY, compactStep.value)
  } catch {
    /* private mode or a full quota */
  }
}

function readAnchor(): ChatAnchor | null {
  try {
    const raw = localStorage.getItem(POS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as {
      x?: unknown
      y?: unknown
      rx?: unknown
      ry?: unknown
    }
    const rx = Number(parsed.rx)
    const ry = Number(parsed.ry)
    if (Number.isFinite(rx) && Number.isFinite(ry)) {
      const clampedRx = Math.min(1, Math.max(0, rx))
      const clampedRy = Math.min(1, Math.max(0, ry))
      const w = 48
      const h = 48
      const sx = Math.max(1, window.innerWidth - w - EDGE * 2)
      const sy = Math.max(1, window.innerHeight - h - EDGE * 2)
      return {
        x: EDGE + clampedRx * sx,
        y: EDGE + clampedRy * sy,
        rx: clampedRx,
        ry: clampedRy,
      }
    }
    const x = Number(parsed.x)
    const y = Number(parsed.y)
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null
    return { x, y, rx: Number.NaN, ry: Number.NaN }
  } catch {
    return null
  }
}

function persistAnchor(): void {
  try {
    if (!anchor.value) localStorage.removeItem(POS_KEY)
    else localStorage.setItem(POS_KEY, JSON.stringify(anchor.value))
  } catch {
    /* private mode or a full quota */
  }
}

function readChatSize(): { w: number; h: number } {
  try {
    const raw = localStorage.getItem(SIZE_KEY)
    if (!raw) return { w: DEFAULT_W, h: DEFAULT_H }
    const parsed = JSON.parse(raw) as { w?: unknown; h?: unknown }
    const w = Number(parsed.w)
    const h = Number(parsed.h)
    if (!Number.isFinite(w) || !Number.isFinite(h)) {
      return { w: DEFAULT_W, h: DEFAULT_H }
    }
    return {
      w: Math.min(ABS_MAX_W, Math.max(MIN_W, Math.round(w))),
      h: Math.min(ABS_MAX_H, Math.max(MIN_H, Math.round(h))),
    }
  } catch {
    return { w: DEFAULT_W, h: DEFAULT_H }
  }
}

function viewportMax(): { maxW: number; maxH: number } {
  return {
    maxW: Math.max(MIN_W, Math.min(ABS_MAX_W, window.innerWidth - 32)),
    maxH: Math.max(MIN_H, Math.min(ABS_MAX_H, window.innerHeight - 96)),
  }
}

function clampSize(w: number, h: number): { w: number; h: number } {
  const { maxW, maxH } = viewportMax()
  return {
    w: Math.round(Math.min(maxW, Math.max(MIN_W, w))),
    h: Math.round(Math.min(maxH, Math.max(MIN_H, h))),
  }
}

function largePreset(): { w: number; h: number } {
  const { maxW, maxH } = viewportMax()
  return {
    w: Math.min(maxW, 640),
    h: Math.min(maxH, Math.round(window.innerHeight * 0.72)),
  }
}

const viewportTick = ref(0)

function onViewportResize(): void {
  viewportTick.value += 1
  syncVisualViewport()
  measureBtn()
  reflowAnchor()
  void measureFootprint()
}

function syncVisualViewport(): void {
  const el = shellEl.value
  const vv = window.visualViewport
  if (!el || !vv) return
  const covered = Math.max(0, window.innerHeight - vv.offsetTop - vv.height)
  kbOffset.value = covered > 80 ? Math.round(covered) : 0
  el.style.setProperty('--chat-vv-h', `${Math.round(vv.height)}px`)
  el.style.setProperty('--chat-vv-top', `${Math.round(vv.offsetTop)}px`)
  el.style.setProperty('--chat-kb', `${kbOffset.value}px`)
}

onMounted(() => {
  window.addEventListener('resize', onViewportResize)
  document.addEventListener('visibilitychange', publishInView)
  syncVisualViewport()
  measureBtn()
  reflowAnchor()
  publishInView()
  window.visualViewport?.addEventListener('resize', syncVisualViewport)
  window.visualViewport?.addEventListener('scroll', syncVisualViewport)
})

onUnmounted(() => {
  window.removeEventListener('resize', onViewportResize)
  document.removeEventListener('visibilitychange', publishInView)
  window.visualViewport?.removeEventListener('resize', syncVisualViewport)
  window.visualViewport?.removeEventListener('scroll', syncVisualViewport)
  chat.setChannelInView('global', false)
  chat.setChannelInView('duel', false)
})

function persistSize(): void {
  try {
    localStorage.setItem(SIZE_KEY, JSON.stringify(requested.value))
  } catch {
    /* private mode or a full quota */
  }
}

function measureBtn(): void {
  const el = toggleEl.value
  if (!el) return
  const w = el.offsetWidth
  const h = el.offsetHeight
  if (w > 0 && h > 0) btnSize.value = { w, h }
}

function clampPixels(pos: { x: number; y: number }): { x: number; y: number } {
  const { w, h } = btnSize.value
  const maxX = Math.max(EDGE, window.innerWidth - w - EDGE)
  const maxY = Math.max(EDGE, window.innerHeight - h - kbOffset.value - EDGE)
  return {
    x: Math.round(Math.min(maxX, Math.max(EDGE, pos.x))),
    y: Math.round(Math.min(maxY, Math.max(EDGE, pos.y))),
  }
}

function freeSpan(): { sx: number; sy: number } {
  const { w, h } = btnSize.value
  return {
    sx: Math.max(1, window.innerWidth - w - EDGE * 2),
    sy: Math.max(1, window.innerHeight - h - EDGE * 2),
  }
}

function anchorFromPixels(pos: { x: number; y: number }): ChatAnchor {
  const clamped = clampPixels(pos)
  const { sx, sy } = freeSpan()
  return {
    x: clamped.x,
    y: clamped.y,
    rx: (clamped.x - EDGE) / sx,
    ry: (clamped.y - EDGE) / sy,
  }
}

function anchorFromRatio(rx: number, ry: number): ChatAnchor {
  const { sx, sy } = freeSpan()
  const clamped = clampPixels({ x: EDGE + rx * sx, y: EDGE + ry * sy })
  return { x: clamped.x, y: clamped.y, rx, ry }
}

function reflowAnchor(): void {
  const current = anchor.value
  if (!current) return
  anchor.value = Number.isFinite(current.rx)
    ? anchorFromRatio(current.rx, current.ry)
    : anchorFromPixels(current)
}

function buttonBox(): {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
} {
  const pos = clampPixels(anchor.value ?? { x: EDGE, y: EDGE })
  const { w, h } = btnSize.value
  const bottom = pos.y + kbOffset.value
  const top = window.innerHeight - bottom - h
  return { left: pos.x, top, right: pos.x + w, bottom: top + h, width: w, height: h }
}

const panelGrow = computed(() => {
  viewportTick.value
  kbOffset.value
  footprint.value
  if (!anchor.value || !chat.open || (props.compact && compactStep.value === 'full')) {
    return { growRight: false, growDown: false }
  }
  const box = buttonBox()
  const fp =
    footprint.value.w > 0 || footprint.value.h > 0
      ? footprint.value
      : props.compact
        ? compactStep.value === 'medium'
          ? {
              w: Math.min(560, window.innerWidth - 24),
              h: Math.min(560, Math.round(window.innerHeight * 0.55)),
            }
          : {
              w: Math.min(320, window.innerWidth - 24),
              h: Math.min(280, Math.round(window.innerHeight * 0.45)),
            }
        : { w: requested.value.w, h: requested.value.h }
  const overflowLeft = box.right - fp.w < EDGE
  const overflowRight = box.left + fp.w > window.innerWidth - EDGE
  let growRight = false
  if (overflowLeft && !overflowRight) growRight = true
  else if (overflowLeft && overflowRight) {
    growRight = window.innerWidth - box.right > box.left
  }
  const gap = 8
  const overflowUp = box.top - gap - fp.h < EDGE
  const overflowDown = box.bottom + gap + fp.h > window.innerHeight - EDGE
  let growDown = false
  if (overflowUp && !overflowDown) growDown = true
  else if (overflowUp && overflowDown) {
    growDown = window.innerHeight - box.bottom > box.top
  }
  return { growRight, growDown }
})

const shellStyle = computed(() => {
  viewportTick.value
  kbOffset.value
  btnSize.value
  if (!anchor.value) return undefined
  const box = buttonBox()
  return {
    left: `${Math.round(box.left)}px`,
    top: `${Math.round(box.top)}px`,
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
  }
})

async function measureFootprint(): Promise<void> {
  await nextTick()
  const panel = panelEl.value
  if (!panel || !chat.open || (props.compact && compactStep.value === 'full')) {
    footprint.value = { w: 0, h: 0 }
    return
  }
  footprint.value = { w: panel.offsetWidth, h: panel.offsetHeight }
}

function anchorFromButton(): ChatAnchor {
  const el = toggleEl.value
  if (!el) return anchorFromPixels({ x: EDGE, y: EDGE })
  const rect = el.getBoundingClientRect()
  return anchorFromPixels({
    x: rect.left,
    y: window.innerHeight - rect.bottom - kbOffset.value,
  })
}

function nudgeAnchor(dx: number, dy: number): void {
  const start = anchor.value ? clampPixels(anchor.value) : anchorFromButton()
  anchor.value = anchorFromPixels({ x: start.x + dx, y: start.y + dy })
  persistAnchor()
}

function resetAnchor(): void {
  anchor.value = null
  persistAnchor()
}

function onTogglePointerDown(event: PointerEvent): void {
  if (event.button !== 0) return
  const handle = event.currentTarget
  if (!(handle instanceof HTMLElement)) return
  handle.setPointerCapture(event.pointerId)
  const originX = event.clientX
  const originY = event.clientY
  const start = anchor.value ? clampPixels(anchor.value) : anchorFromButton()
  let moved = false
  const prevSelect = document.body.style.userSelect

  const onMove = (ev: PointerEvent) => {
    const dx = ev.clientX - originX
    const dy = ev.clientY - originY
    if (!moved && Math.hypot(dx, dy) < 8) return
    if (!moved) {
      moved = true
      dragging.value = true
      document.body.style.userSelect = 'none'
    }
    anchor.value = anchorFromPixels({ x: start.x + dx, y: start.y - dy })
  }
  const onUp = (ev: PointerEvent) => {
    handle.removeEventListener('pointermove', onMove)
    handle.removeEventListener('pointerup', onUp)
    handle.removeEventListener('pointercancel', onUp)
    if (handle.hasPointerCapture(ev.pointerId)) {
      handle.releasePointerCapture(ev.pointerId)
    }
    dragging.value = false
    document.body.style.userSelect = prevSelect
    if (!moved) return
    suppressToggle = true
    anchor.value = anchor.value ? anchorFromPixels(anchor.value) : null
    persistAnchor()
    void measureFootprint()
  }
  handle.addEventListener('pointermove', onMove)
  handle.addEventListener('pointerup', onUp)
  handle.addEventListener('pointercancel', onUp)
}

function onToggleClick(): void {
  if (suppressToggle) {
    suppressToggle = false
    return
  }
  chat.toggle()
}

function onToggleKey(event: KeyboardEvent): void {
  const step = event.shiftKey ? 48 : 16
  if (event.key === 'ArrowLeft') nudgeAnchor(-step, 0)
  else if (event.key === 'ArrowRight') nudgeAnchor(step, 0)
  else if (event.key === 'ArrowUp') nudgeAnchor(0, step)
  else if (event.key === 'ArrowDown') nudgeAnchor(0, -step)
  else if (event.key === 'Home') resetAnchor()
  else return
  event.preventDefault()
  event.stopPropagation()
}

const panelStyle = computed(() => {
  if (props.compact) return undefined
  return {
    width: `${requested.value.w}px`,
    height: `${requested.value.h}px`,
  }
})

const isEnlarged = computed(() => {
  viewportTick.value
  const large = largePreset()
  return (
    requested.value.w >= large.w - 24 && requested.value.h >= large.h - 24
  )
})

const shrinkIcon = computed(() =>
  props.compact ? compactStep.value === 'full' : isEnlarged.value,
)

const sizeLabel = computed(() => {
  if (props.compact) {
    if (compactStep.value === 'normal') return t('chat.global.medium')
    if (compactStep.value === 'medium') return t('chat.global.full')
    return t('chat.global.shrink')
  }
  return isEnlarged.value ? t('chat.global.shrink') : t('chat.global.expand')
})

const awayFromBottom = ref(false)

function logNearBottom(): boolean {
  const el = logEl.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight < 48
}

function syncAwayFromBottom(): void {
  awayFromBottom.value = !logNearBottom()
  publishInView()
}

/**
 * A channel counts as seen only while its log is open on the latest line
 * and this browser tab is visible. Using the rest of the site does not.
 */
function publishInView(): void {
  const visible = document.visibilityState === 'visible'
  const atBottom = logEl.value ? logNearBottom() : false
  chat.setChannelInView(
    'global',
    chat.open && chat.tab === 'global' && visible && atBottom,
  )
  chat.setChannelInView(
    'duel',
    chat.open && chat.tab === 'duel' && chat.duelAvailable && visible && atBottom,
  )
  chat.setChannelInView(
    'dm',
    chat.open && chat.tab === 'dm' && chat.dmActive !== null && visible && atBottom,
  )
}

const jumpUnread = computed(() => {
  if (chat.tab === 'duel' && chat.duelAvailable) return chat.duelUnread
  if (chat.tab === 'dm' && chat.dmActive) return chat.dmUnreadOf(chat.dmActive)
  return chat.globalUnread
})

function jumpToLatest(): void {
  const el = logEl.value
  if (el) el.scrollTop = el.scrollHeight
  awayFromBottom.value = false
  publishInView()
}

async function stickLogIf(stick: boolean): Promise<void> {
  if (!stick) {
    await nextTick()
    syncAwayFromBottom()
    return
  }
  await nextTick()
  const el = logEl.value
  if (el) el.scrollTop = el.scrollHeight
  awayFromBottom.value = false
  publishInView()
}

function toggleSize(): void {
  const stick = logNearBottom()
  if (props.compact) {
    compactStep.value =
      compactStep.value === 'normal'
        ? 'medium'
        : compactStep.value === 'medium'
          ? 'full'
          : 'normal'
    persistCompactStep()
    void stickLogIf(stick)
    return
  }
  if (isEnlarged.value) {
    requested.value = { w: DEFAULT_W, h: DEFAULT_H }
  } else {
    requested.value = largePreset()
  }
  persistSize()
  void stickLogIf(stick)
}

function onGripDown(event: PointerEvent): void {
  if (event.button !== 0 || props.compact) return
  event.preventDefault()
  const handle = event.currentTarget
  if (!(handle instanceof HTMLElement)) return
  handle.setPointerCapture(event.pointerId)
  const stick = logNearBottom()
  const rect = panelEl.value?.getBoundingClientRect()
  const originX = event.clientX
  const originY = event.clientY
  const originW = rect?.width ?? requested.value.w
  const originH = rect?.height ?? requested.value.h
  const prevCursor = document.body.style.cursor
  const prevSelect = document.body.style.userSelect
  document.body.style.cursor = 'nwse-resize'
  document.body.style.userSelect = 'none'

  const growRight = panelGrow.value.growRight
  const growDown = panelGrow.value.growDown
  const onMove = (ev: PointerEvent) => {
    requested.value = clampSize(
      originW + (growRight ? ev.clientX - originX : originX - ev.clientX),
      originH + (growDown ? ev.clientY - originY : originY - ev.clientY),
    )
  }
  const onUp = (ev: PointerEvent) => {
    handle.removeEventListener('pointermove', onMove)
    handle.removeEventListener('pointerup', onUp)
    handle.removeEventListener('pointercancel', onUp)
    if (handle.hasPointerCapture(ev.pointerId)) {
      handle.releasePointerCapture(ev.pointerId)
    }
    document.body.style.cursor = prevCursor
    document.body.style.userSelect = prevSelect
    persistSize()
    void stickLogIf(stick)
  }
  handle.addEventListener('pointermove', onMove)
  handle.addEventListener('pointerup', onUp)
  handle.addEventListener('pointercancel', onUp)
}

function onGripKey(event: KeyboardEvent): void {
  const step = event.shiftKey ? 48 : 24
  let dw = 0
  let dh = 0
  const { growRight, growDown } = panelGrow.value
  if (event.key === 'ArrowLeft') dw = growRight ? -step : step
  else if (event.key === 'ArrowRight') dw = growRight ? step : -step
  else if (event.key === 'ArrowUp') dh = growDown ? -step : step
  else if (event.key === 'ArrowDown') dh = growDown ? step : -step
  else if (event.key === 'Home') {
    requested.value = { w: DEFAULT_W, h: DEFAULT_H }
    persistSize()
    event.preventDefault()
    event.stopPropagation()
    return
  } else return
  event.preventDefault()
  event.stopPropagation()
  const rect = panelEl.value?.getBoundingClientRect()
  requested.value = clampSize(
    (rect?.width ?? requested.value.w) + dw,
    (rect?.height ?? requested.value.h) + dh,
  )
  persistSize()
}

function lineParts(text: string, mentions?: string[]): ChatTextPart[] {
  return splitChatMentions(text, mentions ?? mentionPool.value)
}

function partIsMe(part: ChatTextPart): boolean {
  return (
    part.kind === 'mention' &&
    part.username.toLowerCase() === player.username.trim().toLowerCase()
  )
}

const showChannelTabs = computed(
  () => chat.duelAvailable || chat.dmTabs.length > 0,
)

const visibleLines = computed(() => {
  const dmTab = chat.tab === 'dm' && chat.dmActive !== null
  const duelTab = !dmTab && chat.tab === 'duel' && chat.duelAvailable
  const mapLine = (msg: {
    id: string
    at: number
    username: string
    text: string
    mentions?: string[]
    mine: boolean
    avatar: ReturnType<typeof parseAvatarConfig>
  }) => {
    const parts = lineParts(msg.text, msg.mentions).map((part) =>
      part.kind === 'mention' ? { ...part, me: partIsMe(part) } : part,
    )
    return { ...msg, parts, notice: false as const }
  }
  const source = dmTab && chat.dmActive
    ? chat.dmLines(chat.dmActive).map((msg) =>
        mapLine({
          id: msg.id,
          at: msg.at,
          username: msg.username,
          text: msg.text,
          mine: msg.mine,
          avatar: msg.mine
            ? player.avatar
            : (friends.friendOf(msg.username)?.avatar ?? parseAvatarConfig(undefined)),
        }),
      )
    : duelTab
    ? chat.duelMessages.map((msg) =>
        mapLine({
          id: msg.id,
          at: msg.at,
          username: msg.username,
          text: msg.text,
          mentions: msg.mentions,
          mine: msg.seat === chat.mySeat,
          avatar: parseAvatarConfig(msg.avatar),
        }),
      )
    : props.messages.map((msg) =>
        mapLine({
          id: msg.id,
          at: msg.at,
          username: msg.username,
          text: msg.text,
          mentions: msg.mentions,
          mine: msg.clientId === myClientId || chatNamesMatch(msg.username, player.username),
          avatar: parseAvatarConfig(msg.avatar),
        }),
      )
  const shown = dmTab
    ? source
    : source.filter((msg) => {
        if (msg.mine) return true
        if (!mutes.ready) return false
        return !mutes.names.some((name) => chatNamesMatch(name, msg.username))
      })
  const notes = dmTab
    ? []
    : chat.notices
    .filter((note) => note.tab === (duelTab ? 'duel' : 'global'))
    .map((note) => ({
      id: note.id,
      at: note.at,
      username: '',
      text: note.text,
      mine: false,
      avatar: parseAvatarConfig(undefined),
      parts: [] as Array<ChatTextPart & { me?: boolean }>,
      notice: true as const,
    }))
  return [...shown, ...notes].sort((a, b) => a.at - b.at)
})

const mentionPool = computed(() => {
  if (chat.tab === 'dm' && chat.dmActive) return [chat.dmActive]
  if (chat.tab === 'duel' && chat.duelAvailable) {
    const peer = chat.duelPeer
    if (!peer?.connected || !peer.username.trim()) return []
    return [peer.username]
  }
  return props.onlineUsers
})

const activeMention = computed(() =>
  activeMentionQuery(draft.value, cursor.value),
)

const mentionQuery = computed(() => activeMention.value?.query ?? null)

const suggestions = computed(() => {
  if (mentionQuery.value === null) return []
  return mentionSuggestions(mentionQuery.value, mentionPool.value, player.username)
})

const mentionOpen = computed(
  () => mentionQuery.value !== null && mentionQuery.value !== dismissedQuery.value,
)

watch(mentionQuery, (query) => {
  mentionIndex.value = 0
  if (query !== dismissedQuery.value) dismissedQuery.value = null
})

function syncCursor(): void {
  cursor.value = inputEl.value?.selectionStart ?? draft.value.length
}

function pickMention(name: string): void {
  const active = activeMention.value
  if (!active) return
  const input = inputEl.value
  const at = input?.selectionStart ?? cursor.value
  const next = `${draft.value.slice(0, active.start)}@${name} ${draft.value.slice(at)}`
  draft.value = next.slice(0, 200)
  const pos = Math.min(draft.value.length, active.start + name.length + 2)
  void nextTick(() => {
    input?.focus()
    input?.setSelectionRange(pos, pos)
    cursor.value = pos
    dismissedQuery.value = activeMentionQuery(draft.value, pos)?.query ?? null
  })
}

function onInputKeydown(event: KeyboardEvent): void {
  if (!mentionOpen.value) return
  if (event.key === 'Escape') {
    dismissedQuery.value = mentionQuery.value
    event.preventDefault()
    return
  }
  if (suggestions.value.length === 0) return
  if (event.key === 'ArrowDown') {
    mentionIndex.value = (mentionIndex.value + 1) % suggestions.value.length
    event.preventDefault()
  } else if (event.key === 'ArrowUp') {
    mentionIndex.value =
      (mentionIndex.value - 1 + suggestions.value.length) % suggestions.value.length
    event.preventDefault()
  } else if (event.key === 'Enter' || event.key === 'Tab') {
    const name = suggestions.value[mentionIndex.value]
    if (!name) return
    event.preventDefault()
    pickMention(name)
  }
}

const placeholder = computed(() => {
  if (chat.tab === 'dm' && chat.dmActive) {
    return t('chat.dm.placeholder', { name: chat.dmActive })
  }
  return chat.tab === 'duel' && chat.duelAvailable
    ? t('duel.chat.placeholder')
    : t('chat.global.placeholder')
})

const toggleLabel = computed(() => {
  if (chat.open) return t('chat.global.hide')
  return chat.duelAvailable ? t('chat.show') : t('chat.global.show')
})

const onlineLabel = computed(() =>
  props.onlineCount === null
    ? ''
    : t('chat.global.online', { n: props.onlineCount }),
)

const toggleTitle = computed(() => {
  const move = t('chat.global.move')
  if (chat.open || props.onlineCount === null) return `${toggleLabel.value}. ${move}`
  return `${toggleLabel.value}. ${onlineLabel.value}. ${move}`
})

watch(
  () => [chat.open, compactStep.value, props.compact, requested.value.w, requested.value.h] as const,
  () => {
    measureBtn()
    reflowAnchor()
    void measureFootprint()
  },
  { flush: 'post' },
)

watch(
  () => [visibleLines.value.length, chat.tab, chat.dmActive, chat.open] as const,
  async ([len, tab, dmActive, open], prev) => {
    if (!open) {
      awayFromBottom.value = false
      publishInView()
      return
    }
    const opened = prev == null || !prev[3]
    const tabChanged = prev != null && (prev[1] !== tab || prev[2] !== dmActive)
    const grew = prev != null && len > prev[0]
    const mine = visibleLines.value.at(-1)?.mine === true
    const stick = opened || tabChanged || logNearBottom() || (grew && mine)
    if (!stick) {
      await nextTick()
      syncAwayFromBottom()
      return
    }
    await nextTick()
    const el = logEl.value
    if (el) el.scrollTop = el.scrollHeight
    awayFromBottom.value = false
    publishInView()
  },
)

function readSlashMute(text: string): { muted: boolean; name: string } | null {
  const match = /^\/(mute|unmute)(?:\s+([\s\S]*))?$/i.exec(text)
  if (!match) return null
  return {
    muted: match[1]!.toLowerCase() === 'mute',
    name: (match[2] ?? '').replace(/\s+/g, ' ').trim(),
  }
}

const MUTE_NOTICE_KEY = {
  on: 'chat.global.muteOn',
  off: 'chat.global.muteOff',
  already: 'chat.global.muteAlready',
  notMuted: 'chat.global.unmuteAlready',
  self: 'chat.global.muteSelf',
  missing: 'chat.global.muteMissing',
  usage: 'chat.global.muteUsage',
  fail: 'chat.global.muteFail',
} as const

function noticeMute(kind: keyof typeof MUTE_NOTICE_KEY, name = ''): void {
  const tab = chat.tab === 'duel' && chat.duelAvailable ? 'duel' : 'global'
  chat.pushNotice(tab, t(MUTE_NOTICE_KEY[kind], name ? { name } : undefined))
}

async function runMuteCommand(slash: { muted: boolean; name: string }): Promise<void> {
  if (!slash.name) {
    noticeMute('usage')
    return
  }
  if (chatNamesMatch(slash.name, player.username)) {
    noticeMute('self')
    return
  }
  const token = auth.token
  if (!token) return
  try {
    const result = await mutes.setMuted(token, slash.name, slash.muted)
    if (result.muted && result.changed) noticeMute('on', result.username)
    else if (result.muted) noticeMute('already', result.username)
    else if (result.changed) noticeMute('off', result.username)
    else noticeMute('notMuted', result.username)
  } catch (err) {
    const code = err instanceof ApiError ? err.code : ''
    if (code === 'mute_self') noticeMute('self')
    else if (code === 'user_not_found' || code === 'invalid_username') noticeMute('missing')
    else noticeMute('fail')
  }
}

const onSubmit = () => {
  if (mentionOpen.value && suggestions.value.length > 0) {
    const name = suggestions.value[mentionIndex.value]
    if (name) pickMention(name)
    return
  }
  const text = draft.value.trim()
  if (!text) return
  const slash = readSlashMute(text)
  if (slash) {
    draft.value = ''
    cursor.value = 0
    dismissedQuery.value = null
    void runMuteCommand(slash)
    return
  }
  if (chat.tab === 'dm' && chat.dmActive) chat.sendDm(text)
  else if (chat.tab === 'duel' && chat.duelAvailable) chat.sendDuel(text)
  else emit('send', text)
  draft.value = ''
  cursor.value = 0
  dismissedQuery.value = null
}
</script>

<style scoped>
.global-chat {
  position: fixed;
  right: max(16px, env(safe-area-inset-right, 0px));
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  z-index: 60;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  pointer-events: none;
}

.global-chat.over-duel {
  z-index: 220;
}

.global-chat-panel,
.global-chat-toggle {
  pointer-events: auto;
}

.global-chat-panel {
  position: relative;
  width: min(380px, calc(100vw - 32px));
  max-height: 340px;
  display: flex;
  flex-direction: column;
  background: rgba(15, 23, 42, 0.94);
  border: 1px solid #475569;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  overflow: hidden;
}

.global-chat:not(.compact) .global-chat-panel {
  max-width: calc(100vw - 32px);
  max-height: calc(100dvh - 96px);
}

.global-chat:not(.compact) .global-chat-log-wrap {
  flex: 1 1 auto;
  max-height: none;
}

.global-chat:not(.compact) .global-chat-title,
.global-chat:not(.compact) .global-chat-tab:first-child {
  padding-left: 28px;
}

.global-chat-head .global-chat-online {
  margin-right: 4px;
}

.global-chat-grip {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 28px;
  height: 28px;
  padding: 4px;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: nwse-resize;
  touch-action: none;
}

.global-chat-grip:hover,
.global-chat-grip:focus-visible {
  color: #facc15;
}

.global-chat-grip:focus-visible {
  outline: 2px solid #facc15;
  outline-offset: -2px;
}

.global-chat-size {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  margin: 0 8px 0 0;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  touch-action: manipulation;
}

.global-chat-size:hover,
.global-chat-size:focus-visible {
  color: #facc15;
  background: rgba(255, 255, 255, 0.06);
}

.global-chat-size:focus-visible {
  outline: 2px solid #facc15;
  outline-offset: -2px;
}

.global-chat-head {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.global-chat-title {
  flex: 1;
  min-width: 0;
  padding: 10px 12px 8px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #facc15;
}

.global-chat-online {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 4px;
  flex: 0 0 auto;
  margin: 0 12px 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #e2e8f0;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
}

.global-chat-online-toggle {
  margin-right: 0;
}

.global-chat-online svg {
  display: block;
  color: #facc15;
}

.global-chat-tabs {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: thin;
}

.global-chat-tab {
  position: relative;
  flex: 0 0 auto;
  padding: 10px 8px;
  border: 0;
  background: transparent;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
}

.global-chat-tab.active {
  color: #facc15;
  box-shadow: inset 0 -2px 0 #facc15;
}

.dm-tab {
  display: inline-flex;
  align-items: stretch;
  flex: 0 0 auto;
  max-width: 160px;
}

.dm-tab-btn {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: none;
  letter-spacing: 0;
}

.dm-tab.active .dm-tab-btn {
  color: #facc15;
  box-shadow: inset 0 -2px 0 #facc15;
}

.dm-tab-x {
  border: 0;
  background: transparent;
  color: #94a3b8;
  font-size: 16px;
  line-height: 1;
  padding: 0 6px 0 0;
  cursor: pointer;
}

.dm-tab-x:hover,
.dm-tab-x:focus-visible {
  color: #f8fafc;
}

.global-chat-tab-badge {
  display: inline-block;
  min-width: 16px;
  margin-left: 6px;
  padding: 0 4px;
  border-radius: 999px;
  background: #facc15;
  color: #111827;
  font-size: 10px;
  font-weight: 800;
  line-height: 16px;
  text-align: center;
  text-transform: none;
  letter-spacing: 0;
}

.global-chat-log-wrap {
  position: relative;
  flex: 1 1 140px;
  min-height: 0;
  max-height: 220px;
  display: flex;
  flex-direction: column;
}

.global-chat-log {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.global-chat-jump {
  position: absolute;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #475569;
  background: #1e293b;
  color: #facc15;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.global-chat-jump-count {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #facc15;
  color: #111827;
  font-size: 10px;
  font-weight: 800;
  line-height: 16px;
  text-align: center;
}

.global-chat-jump:hover {
  border-color: #94a3b8;
}

.global-chat-line {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  font-size: 15px;
  line-height: 1.35;
}

.global-chat-line.notice {
  display: block;
}

.global-chat-notice {
  margin: 4px 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.35;
  text-align: center;
}

.global-chat-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.global-chat-line.mine .global-chat-name,
.global-chat-line.mine .global-chat-name:visited {
  color: #86efac;
}

.global-chat-name,
.global-chat-name:visited {
  align-self: flex-start;
  font-size: 13px;
  font-weight: 700;
  color: #93c5fd;
  text-decoration: none;
  cursor: pointer;
}

.global-chat-name:hover,
.global-chat-name:focus-visible {
  text-decoration: underline;
}

.global-chat-name:focus-visible {
  outline: 2px solid #facc15;
  outline-offset: 2px;
  border-radius: 2px;
}

.global-chat-text {
  color: #e2e8f0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.global-chat-mention,
.global-chat-mention:visited {
  color: #facc15;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}

.global-chat-mention:hover,
.global-chat-mention:focus-visible {
  text-decoration: underline;
}

.global-chat-mention:focus-visible {
  outline: 2px solid #facc15;
  outline-offset: 2px;
  border-radius: 2px;
}

.global-chat-mention.me {
  padding: 0 3px;
  border-radius: 4px;
  background: rgba(250, 204, 21, 0.18);
}

.global-chat-mentions {
  flex-shrink: 0;
  max-height: 160px;
  margin: 0;
  padding: 4px;
  overflow-y: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.global-chat-mention-empty {
  padding: 6px 8px;
  color: #94a3b8;
  font-size: 13px;
}

.global-chat-mention-opt {
  display: block;
  width: 100%;
  padding: 6px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #facc15;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.global-chat-mention-opt.active,
.global-chat-mention-opt:hover {
  background: rgba(250, 204, 21, 0.12);
}

.global-chat-form {
  display: flex;
  flex-shrink: 0;
  gap: 6px;
  padding: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.global-chat-input {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #475569;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 15px;
}

.global-chat.compact .global-chat-input {
  font-size: 16px;
}

.global-chat-input:focus {
  outline: none;
  border-color: #facc15;
}

.global-chat-send {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #475569;
  background: #1e293b;
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.global-chat-send:hover {
  border-color: #94a3b8;
}

.global-chat.grow-right {
  align-items: flex-start;
}

.global-chat.placed:not(.step-full) .global-chat-panel {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  left: auto;
  top: auto;
}

.global-chat.placed.grow-right:not(.step-full) .global-chat-panel {
  right: auto;
  left: 0;
}

.global-chat.placed.grow-down:not(.step-full) .global-chat-panel {
  bottom: auto;
  top: calc(100% + 8px);
}

.global-chat.grow-down {
  flex-direction: column-reverse;
}

.global-chat.grow-right:not(.compact) .global-chat-title,
.global-chat.grow-right:not(.compact) .global-chat-tab:first-child {
  padding-left: 12px;
}

.global-chat.grow-right .global-chat-head {
  padding-right: 28px;
}

.global-chat.grow-right .global-chat-grip {
  left: auto;
  right: 0;
}

.global-chat.grow-down .global-chat-grip {
  top: auto;
  bottom: 0;
}

.global-chat.grow-down:not(.grow-right) .global-chat-form {
  padding-left: 28px;
}

.global-chat.grow-down.grow-right .global-chat-form {
  padding-right: 28px;
}

.global-chat.placed.compact.step-full .global-chat-panel,
.global-chat.placed.compact.over-duel.step-full .global-chat-panel {
  position: fixed;
  left: max(8px, env(safe-area-inset-left, 0px));
  right: max(8px, env(safe-area-inset-right, 0px));
  top: var(--chat-top);
  bottom: auto;
  width: auto;
  max-width: none;
  height: var(--chat-room);
  max-height: var(--chat-room);
}

.global-chat.placed.compact.step-medium:not(.over-duel) .global-chat-panel {
  width: min(560px, calc(100vw - var(--chat-side) - var(--chat-side-left)));
  max-width: calc(100vw - var(--chat-side) - var(--chat-side-left));
}

.global-chat-toggle {
  position: relative;
  z-index: 1;
  touch-action: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid #475569;
  background: #1e293b;
  color: #e2e8f0;
  font-size: 15px;
  cursor: pointer;
}

.global-chat-toggle:hover {
  border-color: #94a3b8;
}

.global-chat.dragging .global-chat-toggle {
  cursor: grabbing;
}

.global-chat-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: #facc15;
  color: #111827;
  font-size: 11px;
  font-weight: 800;
  line-height: 18px;
  text-align: center;
}

.global-chat.compact {
  --chat-side: max(12px, env(safe-area-inset-right, 0px));
  --chat-side-left: max(12px, env(safe-area-inset-left, 0px));
  --chat-fab: 48px;
  --chat-gap: 8px;
  --chat-top: calc(
    max(
      var(--app-nav-offset, 0px),
      env(safe-area-inset-top, 0px),
      var(--chat-vv-top, 0px)
    ) + 8px
  );
  --chat-vis: var(--chat-vv-h, 100svh);
  --chat-room: max(
    128px,
    calc(
      var(--chat-vis) - var(--chat-top) -
        max(16px, env(safe-area-inset-bottom, 0px)) - var(--chat-fab) -
        var(--chat-gap)
    )
  );
  --chat-normal-cap: min(280px, 70dvh);
  /* Middle step: halfway up from the small cap, never above the full sheet. */
  --chat-medium-h: max(
    min(var(--chat-normal-cap), var(--chat-room)),
    min(
      560px,
      calc(var(--chat-room) - 24px),
      calc(
        (min(var(--chat-normal-cap), var(--chat-room)) + var(--chat-room)) / 2
      )
    )
  );
  right: var(--chat-side);
  bottom: calc(
    max(16px, env(safe-area-inset-bottom, 0px)) + var(--chat-kb, 0px)
  );
}

.global-chat.compact:not(.over-duel) {
  --chat-normal-cap: min(280px, 56dvh);
}

.global-chat.compact .global-chat-toggle {
  width: 48px;
  height: 48px;
  padding: 0;
  border-radius: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.global-chat.compact .global-chat-size {
  width: 40px;
  height: 40px;
  margin: 2px 4px 2px 0;
}

.global-chat.compact.step-medium .global-chat-size {
  color: #facc15;
}

.global-chat.compact .global-chat-head {
  min-height: 44px;
}

.global-chat.compact .global-chat-tab,
.global-chat.compact .global-chat-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.global-chat.compact .global-chat-panel {
  width: min(320px, calc(100vw - var(--chat-side) - var(--chat-side-left)));
  max-height: min(70dvh, 280px, var(--chat-room));
  transition: width var(--md-motion-medium) var(--md-motion-standard);
}

.global-chat.compact.over-duel .global-chat-panel {
  width: min(
    320px,
    calc(
      100vw - 224px - env(safe-area-inset-right, 0px) -
        env(safe-area-inset-left, 0px)
    )
  );
}

.global-chat.compact:not(.over-duel) .global-chat-panel {
  max-height: min(56dvh, 280px, var(--chat-room));
}

.global-chat.compact.step-medium .global-chat-panel {
  height: var(--chat-medium-h);
  max-height: var(--chat-medium-h);
  min-width: 0;
}

.global-chat.open.compact.step-medium:not(.over-duel):not(.placed) {
  left: var(--chat-side-left);
  right: var(--chat-side);
}

.global-chat.compact.step-medium:not(.over-duel) .global-chat-panel {
  width: min(560px, 100%);
  max-width: 100%;
}

.global-chat.compact.over-duel.step-medium .global-chat-panel {
  width: min(
    480px,
    calc(
      100vw - 212px - env(safe-area-inset-right, 0px) - var(--chat-side-left) -
        20px
    )
  );
}

.global-chat.open.compact.step-full:not(.placed),
.global-chat.open.compact.over-duel.step-full:not(.placed) {
  left: max(8px, env(safe-area-inset-left, 0px));
  right: max(8px, env(safe-area-inset-right, 0px));
}

.global-chat.compact.step-full .global-chat-panel {
  align-self: stretch;
  width: auto;
  max-width: none;
  min-width: 0;
  height: var(--chat-room);
  max-height: var(--chat-room);
}

.global-chat.compact.step-medium .global-chat-log-wrap,
.global-chat.compact.step-full .global-chat-log-wrap {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
}

.global-chat.compact.step-medium .global-chat-log,
.global-chat.compact.step-full .global-chat-log {
  overscroll-behavior: contain;
}

@media (max-height: 500px) {
  .global-chat.compact.step-medium:not(.over-duel) .global-chat-panel {
    width: min(680px, 100%);
  }

  .global-chat.placed.compact.step-medium:not(.over-duel) .global-chat-panel {
    width: min(680px, calc(100vw - var(--chat-side) - var(--chat-side-left)));
  }

  .global-chat.compact.over-duel.step-medium .global-chat-panel {
    width: min(
      640px,
      calc(
        100vw - 212px - env(safe-area-inset-right, 0px) -
          var(--chat-side-left) - 20px
      )
    );
  }
}

@media (max-width: 560px) {
  .global-chat.open.compact.over-duel.step-medium:not(.placed) {
    left: max(8px, env(safe-area-inset-left, 0px));
    right: max(8px, env(safe-area-inset-right, 0px));
  }

  .global-chat.compact.over-duel.step-medium:not(.placed) .global-chat-panel {
    width: 100%;
    max-width: 100%;
  }
}

@media (max-width: 380px) {
  .global-chat.compact .global-chat-tab {
    padding: 10px 4px;
    font-size: 11px;
    letter-spacing: 0.02em;
  }

  .global-chat.compact .global-chat-title {
    padding-right: 6px;
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .global-chat.compact .global-chat-panel {
    transition: none;
  }
}

.chat-fab-icon {
  display: flex;
}
</style>
